import { useState } from 'react';
import { usePutScheduleMutation } from '../../api/schedules';
import type { ScheduleDoc, FloorName } from '../../domain';
import { FLOOR_TO_GROUPS } from '../../domain';
import { ulid } from './ulid';

const FLOORS: FloorName[] = ['ground', 'middle', 'top'];
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

interface Interval { day: typeof DAYS[number]; start: string; end: string; }

export function ScheduleEditor({ deviceId, initial, onSaved, onCancel }: {
  deviceId: string;
  initial?: ScheduleDoc;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<'setback' | 'solar'>(initial?.kind ?? 'setback');
  const [target, setTarget] = useState<string>(initial?.target ?? 'top');
  const [setbackC, setSetbackC] = useState<number>(initial?.setback_c ?? -2);
  const [intervals, setIntervals] = useState<Interval[]>(initial?.intervals as Interval[] ?? [{ day: 'mon', start: '22:00', end: '06:00' }]);
  const [active, setActive] = useState<boolean>(initial?.active ?? true);
  const put = usePutScheduleMutation();

  const validTargets: ReadonlyArray<string> = kind === 'setback' ? FLOORS : FLOORS.flatMap((f) => FLOOR_TO_GROUPS[f]);

  function addRow() { setIntervals((iv) => [...iv, { day: 'mon', start: '08:00', end: '09:00' }]); }
  function removeRow(i: number) { setIntervals((iv) => iv.filter((_, j) => j !== i)); }
  function updateRow(i: number, patch: Partial<Interval>) {
    setIntervals((iv) => iv.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  }

  async function onSubmit() {
    const id = initial?.id ?? `schedule:${kind}:${target}:${ulid()}`;
    const now = new Date().toISOString();
    const doc = {
      id,
      deviceId: initial?.deviceId ?? deviceId,
      kind, target,
      intervals,
      ...(kind === 'setback' ? { setback_c: setbackC } : {}),
      active,
      created_at: initial?.created_at ?? now,
      updated_at: now,
    } as ScheduleDoc;
    await put.mutateAsync(doc);
    onSaved();
  }

  return (
    <div className="card">
      <h3>{initial ? 'Edit schedule' : 'New schedule'}</h3>
      <div style={{ display: 'grid', gap: 'var(--sp-2)', gridTemplateColumns: '1fr 1fr', marginTop: 'var(--sp-3)' }}>
        <label>Kind
          <select value={kind} onChange={(e) => { const k = e.target.value as 'setback' | 'solar'; setKind(k); setTarget(k === 'setback' ? 'top' : 'top_sun'); }}>
            <option value="setback">Setback (per floor)</option>
            <option value="solar">Solar (per group)</option>
          </select>
        </label>
        <label>Target
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            {validTargets.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        {kind === 'setback' && (
          <label>Setback °C
            <input type="number" step="0.5" value={setbackC} onChange={(e) => setSetbackC(parseFloat(e.target.value))} />
          </label>
        )}
        <label>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> active
        </label>
      </div>

      <h3 style={{ marginTop: 'var(--sp-4)' }}>Intervals</h3>
      {intervals.map((iv, i) => (
        <div key={i} style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
          <select value={iv.day} onChange={(e) => updateRow(i, { day: e.target.value as Interval['day'] })}>
            {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <input type="time" value={iv.start} onChange={(e) => updateRow(i, { start: e.target.value })} />
          <span style={{ alignSelf: 'center' }}>–</span>
          <input type="time" value={iv.end} onChange={(e) => updateRow(i, { end: e.target.value })} />
          <button className="secondary" onClick={() => removeRow(i)}>✕</button>
        </div>
      ))}
      <button className="secondary" onClick={addRow} style={{ marginTop: 'var(--sp-2)' }}>+ Add interval</button>

      <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-4)' }}>
        <button onClick={onSubmit} disabled={put.isPending}>{put.isPending ? 'Saving…' : 'Save'}</button>
        <button className="secondary" onClick={onCancel}>Cancel</button>
      </div>
      {put.isError && <div className="field-error">{(put.error as Error).message}</div>}
    </div>
  );
}
